import type { Lang } from "../i18n/i18n";
import { useUiLanguage } from "../ui/uiText";
import type { ClientErrorRecoveryLabels } from "./clientErrorTypes";

/**
 * Texte der Fehleransicht und des kopierten Fehlerberichts für alle Produkte. Produkte überschreiben
 * nur fachlich abweichende Texte, z. B. das Ziel von `backToDashboard`.
 */
export const clientErrorLabels: Record<Lang, ClientErrorRecoveryLabels> = {
  de: {
    anErrorOccurred: "Ein Fehler ist aufgetreten",
    backToDashboard: "zurück zur Übersicht",
    close: "schließen",
    copyFailed: "Kopieren fehlgeschlagen",
    copyErrorReport: "Fehlerbericht kopieren",
    developerInformation: "Entwicklerinformationen",
    errorReportCopied: "Fehlerbericht kopiert",
    reloadPage: "Seite neu laden",
    errorId: "Fehler-ID",
    requestId: "Anfrage-ID",
    timestamp: "Zeitpunkt",
    currentPath: "Seite",
    errorLocation: "Fehlerstelle",
    moduleHint: "Modul",
    errorMessage: "Fehlermeldung",
    errorCause: "Ursache",
    url: "Adresse",
    userAgent: "Browser",
    stack: "Stacktrace",
    componentStack: "Komponentenstack",
  },
  en: {
    anErrorOccurred: "An error occurred",
    backToDashboard: "back to overview",
    close: "close",
    copyFailed: "Copy failed",
    copyErrorReport: "Copy error report",
    developerInformation: "Developer information",
    errorReportCopied: "Error report copied",
    reloadPage: "Reload page",
    errorId: "Error ID",
    requestId: "Request ID",
    timestamp: "Timestamp",
    currentPath: "Page",
    errorLocation: "Error location",
    moduleHint: "Module",
    errorMessage: "Error message",
    errorCause: "Cause",
    url: "Address",
    userAgent: "Browser",
    stack: "Stack trace",
    componentStack: "Component stack",
  },
  it: {
    anErrorOccurred: "Si è verificato un errore",
    backToDashboard: "torna alla panoramica",
    close: "chiudi",
    copyFailed: "Copia non riuscita",
    copyErrorReport: "Copia il rapporto di errore",
    developerInformation: "Informazioni per sviluppatori",
    errorReportCopied: "Rapporto di errore copiato",
    reloadPage: "Ricarica la pagina",
    errorId: "ID errore",
    requestId: "ID richiesta",
    timestamp: "Data e ora",
    currentPath: "Pagina",
    errorLocation: "Posizione dell’errore",
    moduleHint: "Modulo",
    errorMessage: "Messaggio di errore",
    errorCause: "Causa",
    url: "Indirizzo",
    userAgent: "Browser",
    stack: "Traccia dello stack",
    componentStack: "Stack dei componenti",
  },
  ru: {
    anErrorOccurred: "Произошла ошибка",
    backToDashboard: "вернуться к обзору",
    close: "закрыть",
    copyFailed: "Не удалось скопировать",
    copyErrorReport: "Скопировать отчёт об ошибке",
    developerInformation: "Информация для разработчиков",
    errorReportCopied: "Отчёт об ошибке скопирован",
    reloadPage: "Перезагрузить страницу",
    errorId: "ID ошибки",
    requestId: "ID запроса",
    timestamp: "Дата и время",
    currentPath: "Страница",
    errorLocation: "Место ошибки",
    moduleHint: "Модуль",
    errorMessage: "Сообщение об ошибке",
    errorCause: "Причина",
    url: "Адрес",
    userAgent: "Браузер",
    stack: "Трассировка стека",
    componentStack: "Стек компонентов",
  },
  uk: {
    anErrorOccurred: "Сталася помилка",
    backToDashboard: "повернутися до огляду",
    close: "закрити",
    copyFailed: "Не вдалося скопіювати",
    copyErrorReport: "Скопіювати звіт про помилку",
    developerInformation: "Інформація для розробників",
    errorReportCopied: "Звіт про помилку скопійовано",
    reloadPage: "Перезавантажити сторінку",
    errorId: "ID помилки",
    requestId: "ID запиту",
    timestamp: "Дата й час",
    currentPath: "Сторінка",
    errorLocation: "Місце помилки",
    moduleHint: "Модуль",
    errorMessage: "Повідомлення про помилку",
    errorCause: "Причина",
    url: "Адреса",
    userAgent: "Браузер",
    stack: "Трасування стека",
    componentStack: "Стек компонентів",
  },
};

export function clientErrorLabelsFor(lang: string, overrides?: Partial<ClientErrorRecoveryLabels>): ClientErrorRecoveryLabels {
  const base = clientErrorLabels[lang as Lang] ?? clientErrorLabels.en;
  return overrides ? { ...base, ...overrides } : base;
}

/** Texte in der Sprache des Kits (`UiLanguageProvider`). */
export function useClientErrorLabels(overrides?: Partial<ClientErrorRecoveryLabels>): ClientErrorRecoveryLabels {
  return clientErrorLabelsFor(useUiLanguage(), overrides);
}
